import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Divider,
  Link
} from '@mui/material';

function Footer() {
  const partners = [
    {
      name: 'United Nations Development Programme',
      logo: '/UNDP_logo.png',
      alt: 'UNDP Logo'
    },
    {
      name: 'Lagos State Government',
      logo: '/lasg_logo.png',
      alt: 'Lagos State Government Logo'
    },
    {
      name: 'Internet Society Nigerian Chapter',
      logo: '/isoc_logo.png',
      alt: 'Internet Society Nigerian Chapter Logo'
    },
    {
      name: 'Nigeria Computer Society, Lagos Chapter',
      logo: '/ncs-lagos.jpeg',
      alt: 'Nigeria Computer Society, Lagos Chapter Logo'
    },
    {
      name: 'Nigeria Association Of Computing Students',
      logo: '/nacos_logo.jpg',
      alt: 'Nigeria Association Of Computing Students Logo'
    },
    {
      name: 'Plus Incubation Hub',
      logo: '/plusincubationhub.jpg',
      alt: 'Plus Incubation Hub Logo'
    },
    {
      name: 'School of Grant Success',
      logo: '/schoolofgrantsuccess.jpg',
      alt: 'School of Grant Success Logo'
    }
  ];

  return (
    <Box
      component="footer"
      sx={(theme) => ({
        bgcolor: theme.palette.grey[900],
        color: theme.palette.getContrastText(theme.palette.grey[900]),
        py: 6,
        mt: 8,
        borderTop: '4px solid #1976d2',
        boxShadow: '0 -2px 10px rgba(25, 118, 210, 0.3)'
      })}
    >
      <Container maxWidth="lg">
        {/* Partners Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 4, fontWeight: 'bold' }}
          >
            Our Partners
          </Typography>

          {/* Modern Partners Grid */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 2, sm: 3, md: 4 },
            flexWrap: 'wrap',
            mb: 3,
            px: { xs: 2, sm: 3 }
          }}>
            {partners.map((partner, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  }
                }}
              >
                {/* White background card for logo */}
                <Box
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 60, sm: 80, md: 100 },
                    height: { xs: 60, sm: 80, md: 100 },
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    }
                  }}
                >
                  <img
                    src={partner.logo}
                    alt={partner.alt}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                
                {/* Partner name below */}
                <Typography
                  variant="caption"
                  textAlign="center"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    opacity: 0.8,
                    maxWidth: { xs: 70, sm: 90, md: 100 },
                    lineHeight: 1.3
                  }}
                >
                  {partner.name}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Animated scrolling line */}
          <Box sx={{
            position: 'relative',
            height: 3,
            bgcolor: 'rgba(25, 118, 210, 0.2)',
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              bgcolor: '#1976d2',
              animation: 'scroll 3s linear infinite',
              '@keyframes scroll': {
                '0%': { left: '-100%' },
                '100%': { left: '100%' }
              }
            }} />
          </Box>
        </Box>

        <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 4 }} />

        {/* Footer Content */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Lagos Impact Hackathon
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
              The Lagos Impact Hackathon (LIH) is an innovation summit designed to accelerate technology-driven solutions for some of Nigeria’s most pressing socio-economic and environmental challenges.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              © 2025 Lagos Impact Hackathon. All rights reserved.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link href="/" color="inherit" sx={{ opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Home
              </Link>
              <Link href="/about" color="inherit" sx={{ opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                About
              </Link>
              <Link href="/tracks" color="inherit" sx={{ opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Tracks
              </Link>
              <Link href="/register" color="inherit" sx={{ opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}>
                Register
              </Link>
              <Link href="/ceremony" color="inherit" sx={{ opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1, color: 'secondary.main' } }}>
                Closing Ceremony 🏆
              </Link>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Contact
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
              📧 hello@lagosimpacthackathon.org
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
              📱 +234 802 320 3573
            </Typography>

          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Box sx={{
          mt: 4,
          pt: 2,
          borderTop: '1px solid rgba(25, 118, 210, 0.3)',
          textAlign: 'center'
        }}>
          <Typography variant="body2" sx={{ opacity: 0.6 }}>
            Tech Innovation for Sustainable Impact
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
